import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTimeContext } from "./DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RefreshCw, User, Building2, CheckCircle2, AlertTriangle, Server, UserPlus, Trash2, Shield, KeyRound, ToggleLeft, ToggleRight, ChevronDown, Pencil } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const { token } = useTimeContext();
  const [syncStatus, setSyncStatus] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectSaving, setProjectSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [projectsOpen, setProjectsOpen] = useState(true);
  const [syncOpen, setSyncOpen] = useState(false);
  const [platformOpen, setPlatformOpen] = useState(false);
  const [editingAlias, setEditingAlias] = useState(null);
  const [aliasValue, setAliasValue] = useState("");

  // New user form
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("Viewer");
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetPassword, setResetPassword] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [syncRes, usersRes, projRes] = await Promise.all([
        axios.get(`${API}/sync/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/projects/all`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setSyncStatus(syncRes.data.last_sync);
      setUsers(usersRes.data.users || []);
      setProjects(projRes.data.projects || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module constants, setters are stable
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUsername.trim() || !newPassword.trim()) { setFormError("Username and password required"); return; }
    setFormLoading(true);
    setFormError("");
    try {
      await axios.post(`${API}/users`, { username: newUsername.trim(), password: newPassword, role: newRole }, { headers: { Authorization: `Bearer ${token}` } });
      setNewUsername(""); setNewPassword(""); setNewRole("Viewer"); setShowAddUser(false);
      fetchData();
    } catch (err) {
      setFormError(err.response?.data?.detail || "Failed to create user");
    } finally { setFormLoading(false); }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Delete user "${username}"?`)) return;
    try {
      await axios.delete(`${API}/users/${username}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || "Failed to delete"); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetPassword.trim()) return;
    try {
      await axios.post(`${API}/users/reset-password`, { username: resetUser, new_password: resetPassword }, { headers: { Authorization: `Bearer ${token}` } });
      setResetUser(null); setResetPassword("");
      alert(`Password reset for ${resetUser}`);
    } catch (err) { alert(err.response?.data?.detail || "Failed to reset"); }
  };

  const toggleProject = (id) => {
    setProjects(prev => prev.map(p => p.id === id ? { ...p, active: !p.active } : p));
  };

  const saveAlias = async (projectName) => {
    try {
      await axios.post(`${API}/projects/alias`, { project_name: projectName, alias: aliasValue }, { headers: { Authorization: `Bearer ${token}` } });
      setProjects(prev => prev.map(p => p.name === projectName ? { ...p, alias: aliasValue } : p));
      setEditingAlias(null);
      setAliasValue("");
    } catch (err) { alert(err.response?.data?.detail || "Failed to save alias"); }
  };

  const saveProjectConfig = async () => {
    setProjectSaving(true);
    try {
      const activeIds = projects.filter(p => p.active).map(p => p.id);
      await axios.post(`${API}/projects/config`, { active_project_ids: activeIds }, { headers: { Authorization: `Bearer ${token}` } });
      alert(`Saved ${activeIds.length} active projects. KPI data will now use this filter.`);
    } catch (err) { alert(err.response?.data?.detail || "Failed to save"); }
    finally { setProjectSaving(false); }
  };

  const sync = syncStatus || {};

  const formatDate = (iso) => {
    if (!iso) return '--';
    try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-4 sm:space-y-6 overflow-hidden" data-testid="settings-page">
      <div>
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>Settings</h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Platform configuration, users, and system information</p>
      </div>

      {/* User Management */}
      <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm" data-testid="user-management">
        <CardHeader className="pb-3 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Shield className="h-4 w-4 text-blue-600" /><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>User Management</CardTitle></div>
            <button onClick={() => setShowAddUser(!showAddUser)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-[#1D4ED8] hover:bg-[#1E40AF] rounded-lg transition-colors" data-testid="add-user-btn">
              <UserPlus className="h-3.5 w-3.5" />Add User
            </button>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {/* Add User Form */}
          {showAddUser && (
            <form onSubmit={handleAddUser} className="mb-4 p-4 bg-blue-50 rounded-xl border border-blue-100" data-testid="add-user-form">
              <p className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-3">New User</p>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                <input type="text" placeholder="Username" value={newUsername} onChange={e => setNewUsername(e.target.value)} className="h-9 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" data-testid="new-username" />
                <input type="password" placeholder="Password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="h-9 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" data-testid="new-password" />
                <select value={newRole} onChange={e => setNewRole(e.target.value)} className="h-9 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" data-testid="new-role">
                  <option value="Viewer">Viewer</option>
                  <option value="Manager">Manager</option>
                  <option value="Admin">Admin</option>
                </select>
                <button type="submit" disabled={formLoading} className="h-9 px-4 text-xs font-semibold text-white bg-[#1D4ED8] hover:bg-[#1E40AF] rounded-lg transition-colors disabled:opacity-50" data-testid="submit-user-btn">
                  {formLoading ? "Creating..." : "Create User"}
                </button>
              </div>
              {formError && <p className="text-xs text-red-600 mt-2">{formError}</p>}
            </form>
          )}

          {/* Users List */}
          <div className="space-y-1.5">
            {users.map((u, i) => (
              <div key={i}>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`user-row-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className={`h-9 w-9 rounded-lg flex items-center justify-center text-sm font-bold text-white ${u.is_system ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-slate-400'}`}>
                      {u.username?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{u.username}</p>
                      <p className="text-[10px] text-slate-400">{u.role}{u.is_system ? ' (System)' : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${u.active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {u.active !== false ? 'Active' : 'Inactive'}
                    </span>
                    {!u.is_system && (
                      <button onClick={() => { setResetUser(u.username); setResetPassword(""); }} className="p-1.5 hover:bg-blue-100 rounded-md text-slate-400 hover:text-blue-600 transition-colors" title="Reset Password">
                        <KeyRound className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {!u.is_system && (
                      <button onClick={() => handleDeleteUser(u.username)} className="p-1.5 hover:bg-red-100 rounded-md text-slate-400 hover:text-red-600 transition-colors" data-testid={`delete-user-${i}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                {resetUser === u.username && (
                  <form onSubmit={handleResetPassword} className="flex items-center gap-2 ml-12 mt-1 p-2 bg-blue-50 rounded-lg border border-blue-100">
                    <KeyRound className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                    <input type="password" placeholder="New password" value={resetPassword} onChange={e => setResetPassword(e.target.value)} className="h-8 px-2.5 text-xs bg-white border border-slate-200 rounded-md flex-1 focus:outline-none focus:border-blue-400" />
                    <button type="submit" className="h-8 px-3 text-[10px] font-bold text-white bg-[#1D4ED8] rounded-md">Reset</button>
                    <button type="button" onClick={() => setResetUser(null)} className="h-8 px-2 text-[10px] text-slate-500 hover:text-slate-700">Cancel</button>
                  </form>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Project Configuration */}
      <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm" data-testid="project-config">
        <button onClick={() => setProjectsOpen(!projectsOpen)} className="w-full">
          <CardHeader className="pb-3 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-indigo-600" /><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Active Projects</CardTitle></div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-slate-400">{projects.filter(p => p.active).length} / {projects.length} active</span>
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${projectsOpen ? 'rotate-180' : ''}`} />
              </div>
            </div>
          </CardHeader>
        </button>
        {projectsOpen && (
        <CardContent className="pt-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] text-slate-400">Toggle projects on/off. Click the pencil icon to set a display alias.</p>
            <button onClick={saveProjectConfig} disabled={projectSaving} className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold text-white bg-[#1D4ED8] hover:bg-[#1E40AF] rounded-lg transition-colors disabled:opacity-50">
              {projectSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {projects.map((p) => (
              <div key={p.id}>
                <div className={`flex items-center justify-between p-2.5 rounded-lg transition-colors ${p.active ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <button onClick={() => toggleProject(p.id)} className="flex-shrink-0">
                      {p.active ? <ToggleRight className="h-5 w-5 text-emerald-600" /> : <ToggleLeft className="h-5 w-5 text-slate-300" />}
                    </button>
                    <div className="min-w-0">
                      <span className="text-xs font-medium text-slate-800 truncate block">{p.name}</span>
                      {p.alias && <span className="text-[9px] text-blue-500 block">Alias: {p.alias}</span>}
                    </div>
                    {p.city && <span className="text-[9px] text-slate-400 flex-shrink-0">{p.city}</span>}
                  </div>
                  <button onClick={() => { setEditingAlias(p.name); setAliasValue(p.alias || ""); }} className="p-1 hover:bg-white rounded-md text-slate-400 hover:text-blue-600 transition-colors flex-shrink-0" title="Set Alias" data-testid={`alias-btn-${p.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </div>
                {editingAlias === p.name && (
                  <div className="flex items-center gap-2 ml-8 mt-1 p-2 bg-blue-50 rounded-lg border border-blue-100">
                    <input type="text" placeholder="Display alias" value={aliasValue} onChange={e => setAliasValue(e.target.value)} className="h-8 px-2.5 text-xs bg-white border border-slate-200 rounded-md flex-1 focus:outline-none focus:border-blue-400" data-testid={`alias-input-${p.id}`} />
                    <button onClick={() => saveAlias(p.name)} className="h-8 px-3 text-[10px] font-bold text-white bg-[#1D4ED8] rounded-md" data-testid={`alias-save-${p.id}`}>Save</button>
                    <button onClick={() => setEditingAlias(null)} className="h-8 px-2 text-[10px] text-slate-500 hover:text-slate-700">Cancel</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
        )}
      </Card>

      {/* Platform Info */}
      <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm">
        <button onClick={() => setPlatformOpen(!platformOpen)} className="w-full">
          <CardHeader className="pb-3 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-blue-600" /><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Platform Information</CardTitle></div>
              <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${platformOpen ? 'rotate-180' : ''}`} />
            </div>
          </CardHeader>
        </button>
        {platformOpen && (
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-slate-50 rounded-xl p-4"><p className="text-[10px] font-semibold text-slate-400 uppercase">Application</p><p className="text-sm font-bold text-slate-900 mt-1" style={{ fontFamily: 'Manrope' }}>Nexus</p><p className="text-[10px] text-slate-400">Realty Intelligence</p></div>
            <div className="bg-slate-50 rounded-xl p-4"><p className="text-[10px] font-semibold text-slate-400 uppercase">Version</p><p className="text-sm font-bold text-slate-900 mt-1" style={{ fontFamily: 'Manrope' }}>3.0</p><p className="text-[10px] text-slate-400">Production</p></div>
            <div className="bg-slate-50 rounded-xl p-4"><p className="text-[10px] font-semibold text-slate-400 uppercase">Organization</p><p className="text-sm font-bold text-slate-900 mt-1" style={{ fontFamily: 'Manrope' }}>Radiance Realty</p><p className="text-[10px] text-slate-400">Chennai, India</p></div>
            <div className="bg-slate-50 rounded-xl p-4"><p className="text-[10px] font-semibold text-slate-400 uppercase">AI Engine</p><p className="text-sm font-bold text-slate-900 mt-1" style={{ fontFamily: 'Manrope' }}>Nexus AI</p><p className="text-[10px] text-slate-400">GPT-4o powered</p></div>
          </div>
        </CardContent>
        )}
      </Card>

      {/* Data Sync Status */}
      <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm">
        <button onClick={() => setSyncOpen(!syncOpen)} className="w-full">
          <CardHeader className="pb-3 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><RefreshCw className="h-4 w-4 text-emerald-500" /><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Data Sync Status</CardTitle></div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5"><div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /><span className="text-[10px] font-medium text-emerald-600">Auto-sync active</span></div>
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${syncOpen ? 'rotate-180' : ''}`} />
              </div>
            </div>
          </CardHeader>
        </button>
        {syncOpen && (
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-emerald-50 rounded-xl p-3"><p className="text-[10px] font-semibold text-emerald-600 uppercase">Frequency</p><p className="text-sm font-bold text-emerald-900 mt-0.5">Every 4 hours</p></div>
            <div className="bg-blue-50 rounded-xl p-3"><p className="text-[10px] font-semibold text-blue-600 uppercase">Last Sync</p><p className="text-sm font-bold text-blue-900 mt-0.5">{formatDate(sync.timestamp)}</p></div>
            <div className="bg-slate-50 rounded-xl p-3"><p className="text-[10px] font-semibold text-slate-400 uppercase">Duration</p><p className="text-sm font-bold text-slate-900 mt-0.5">{sync.duration_seconds ? `${sync.duration_seconds}s` : '--'}</p></div>
            <div className="bg-slate-50 rounded-xl p-3"><p className="text-[10px] font-semibold text-slate-400 uppercase">Source</p><p className="text-sm font-bold text-slate-900 mt-0.5">SQL Server</p><p className="text-[10px] text-slate-400">IN4RE Database</p></div>
          </div>
          {sync.results && (
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Table Sync Details</p>
              <div className="space-y-1.5">
                {sync.results.map((r, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      {r.status === 'ok' ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <AlertTriangle className="h-3.5 w-3.5 text-red-500" />}
                      <span className="text-xs font-medium text-slate-700">{r.table}</span>
                    </div>
                    <span className="text-xs text-slate-500">{r.rows?.toLocaleString('en-IN') || 0} rows</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
        )}
      </Card>

      {/* System */}
      <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm">
        <CardHeader className="pb-3 border-b border-slate-100">
          <div className="flex items-center gap-2"><Server className="h-4 w-4 text-slate-500" /><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>System Status</CardTitle></div>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {[['Backend', 'Running'], ['Database', 'Connected'], ['AI Engine', 'Online'], ['SQL Server', 'Connected'], ['PWA', 'Enabled']].map(([name, status]) => (
              <div key={name} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                <span className="text-xs text-slate-500">{name}</span>
                <span className="text-xs font-medium text-emerald-600">{status}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
